import type { Transition } from "motion/react";

/**
 * Motion presets for Ledgerloom.
 * Springs, durations, easings, and staggers match Arc's published free motion
 * tokens (MIT, Copyright (c) 2026 Elia Kuratli). See THIRD_PARTY_NOTICES.md.
 * State springs use bounce 0 so indicators settle without overshoot.
 */
export const motionTokens = {
  duration: {
    instant: 0.12,
    fast: 0.16,
    exit: 0.18,
    standard: 0.24,
    considered: 0.48,
  },
  ease: {
    enter: [0.16, 1, 0.3, 1],
    standard: [0.22, 1, 0.36, 1],
    exit: [0.7, 0, 0.84, 0],
    inOut: [0.65, 0, 0.35, 1],
  },
  spring: {
    snappy: { type: "spring", visualDuration: 0.26, bounce: 0 },
    smooth: { type: "spring", visualDuration: 0.4, bounce: 0 },
    morph: { type: "spring", visualDuration: 0.42, bounce: 0 },
    responsive: { type: "spring", stiffness: 520, damping: 44 },
    gentle: { type: "spring", stiffness: 340, damping: 34 },
  },
  stagger: {
    char: 0.016,
    word: 0.04,
    item: 0.035,
    line: 0.08,
  },
} as const;

export const pressScale = 0.97;

export function enterTransition(reduce: boolean | null): Transition {
  if (reduce) return { duration: motionTokens.duration.fast, ease: motionTokens.ease.standard };
  return { duration: motionTokens.duration.standard, ease: motionTokens.ease.enter };
}

export function exitTransition(reduce: boolean | null): Transition {
  if (reduce) return { duration: motionTokens.duration.instant, ease: motionTokens.ease.inOut };
  // 180ms, shorter than the 240ms entrance. The shared exit curve holds full
  // opacity and then snaps away, so panels use the in-out curve instead.
  return { duration: motionTokens.duration.exit, ease: motionTokens.ease.inOut };
}

export function indicatorTransition(reduce: boolean | null): Transition {
  if (reduce) return { duration: 0 };
  return motionTokens.spring.snappy;
}

export function glideTransition(reduce: boolean | null): Transition {
  if (reduce) return { duration: 0 };
  return motionTokens.spring.morph;
}
